/**
 * Browsing without an account, which is now the normal state.
 *
 * The app used to open on a sign-in form, which asks someone to prove who
 * they are before they have been given any reason to care. The owner's words
 * were "remove the login screen from beginning it will only be required
 * before sending the request take it inside". So the door is open: language,
 * intent, browsing, prices, building a request. The code is asked for once,
 * at the moment the request is SENT, because that is the first point it buys
 * anything -- for them or for us.
 *
 * Two things are remembered per browser, and neither is worth a server round
 * trip: whether the welcome has been answered, and what the person said they
 * came for. Both reads are wrapped because a private window throws instead of
 * returning null, and a welcome screen that crashes the app is worse than a
 * welcome screen that shows twice.
 */

import { IS_DIRECTORY } from './market'

const SEEN = 'tb.mobile.welcomed'
const INTENT = 'tb.mobile.intent'

/** What someone said they came to do. Charter is first because it is what a
 *  group arriving in Makkah actually wants. */
export type Intent = 'charter' | 'seat' | 'send'
export const INTENTS: Intent[] = ['charter', 'seat', 'send']

function read(k: string): string | null {
  try { return localStorage.getItem(k) } catch { return null }
}
function write(k: string, v: string): void {
  try { localStorage.setItem(k, v) } catch { /* private window; not worth failing over */ }
}

export function hasWelcomed(): boolean { return read(SEEN) === '1' }

export function savedIntent(): Intent {
  const v = read(INTENT)
  return (INTENTS as string[]).includes(v ?? '') ? (v as Intent) : 'charter'
}

export function completeWelcome(intent: Intent): void {
  write(INTENT, intent)
  write(SEEN, '1')
}

/**
 * Show it again.
 *
 * It is answered once per browser and then never nags, which is right --
 * and it left no way back at all, which is not. The mockup's own last line
 * is "You can change both later in Account", so Account is where this is
 * called from.
 */
export function reopenWelcome(): void {
  try { localStorage.removeItem(SEEN) } catch { /* private window */ }
}

/** Where a given intent starts. Send and the two passenger intents share one
 *  set of places (§53.3), so this only picks the opening screen. */
export function landingFor(intent: Intent): string {
  /*
   * In the DIRECTORY build there is one door, and it is the carrier list.
   *
   * The three intents all end in the same place now -- a licensed company
   * who charters, carries goods or seats people, decided with him on his
   * own site. Sending "book a seat" to a /travel that no longer exists
   * would land on the catch-all, which looks like a broken tab.
   */
  if (IS_DIRECTORY) return '/'
  if (intent === 'charter') return '/charter'
  return intent === 'send' ? '/send' : '/travel'
}
